import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSite } from '../context/SiteContext';
import TextMarquee from '../components/TextMarquee';
import ImageMarquee from '../components/ImageMarquee';
import EvokePillars from '../components/EvokePillars';
import { usePageImages } from '../hooks/usePageImages';
import useLanes from '../hooks/useLanes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewHome = () => {
  const { language } = useLanguage();
  const { site } = useSite();
  const [products, setProducts] = useState([]);
  const { getImage, getImages, getImageWithPosition } = usePageImages('home');
  const { lanes: dynamicLanes, loading: lanesLoading } = useLanes();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const ventures = [
    { id: 'ciano-gourmet', name: 'Ciano Gourmet', tagline: language === 'es' ? 'Catering premium + excelencia experiencial' : 'Premium catering meets experiential excellence' },
    { id: 'ciano-bites', name: 'Ciano Bites', tagline: language === 'es' ? 'Catering corporativo moderno' : 'Modern corporate catering, reimagined' },
    { id: 'staffdo', name: 'StaffDO', tagline: language === 'es' ? 'Marketplace de talento' : 'Dominican Republic staffing marketplace' },
    { id: 'kora-os', name: 'Kora OS', tagline: language === 'es' ? 'Sistema operativo para hospitalidad' : 'Operating system for hospitality' },
  ];

  const ownerName = site?.owner_name || 'Margaret Núñez';
  const founderImage = getImageWithPosition('founder');
  const ownerPhoto = founderImage.url || site?.owner_photo || 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80';
  const founderPosition = founderImage.position || '50% 50%';
  const galleryImages = getImages('gallery');

  const content = {
    en: {
      heroOverline: 'Founder / Creative Director',
      heroHeadline: 'Experiences That People Remember',
      heroDescription: 'Campaign moments, live production, and experience systems. Rooted in hospitality and operational excellence.',
      heroCta1: 'Partner With Me',
      heroCta2: 'Selected Moments',
      heroStats: ['4 Ventures', 'Santo Domingo + NYC', '10+ Years in Hospitality'],
      
      builderHeadline: 'I BUILD EXPERIENCES, SYSTEMS AND BRAND MOMENTS FROM CONCEPT TO EXECUTION.',
      builderSubheadline: 'Across hospitality, events and tech-enabled platforms.',
      builderDescription: 'Unleash the full potential of your events with comprehensive design, planning, and creative direction tailored to your unique needs.',
      builderCta: 'Explore My Services',
      builderPrompt: 'Ready to create something unforgettable?',

      lanesTitle: 'Three Lanes',
      lanesOverline: 'Creative Direction',
      lanes: [
        { num: '01', title: 'Campaign & Launch', outcome: 'Brand moments that live beyond the room.', bullets: ['Creative direction + rollout', 'Campaign production', 'Content architecture'] },
        { num: '02', title: 'Live Production', outcome: 'Events designed for impact—and assets after.', bullets: ['Experience design', 'Culinary curation', 'Brand activations'] },
        { num: '03', title: 'Experience Systems', outcome: 'Operational excellence that scales.', bullets: ['Platform development', 'Team workflows', 'Process design'] }
      ],

      manifestoTitle: 'Words We Live By',
      manifestoLines: [
        'Hospitality trained the standard.',
        'Systems protect it at scale.',
        'Intention beats perfection.',
        'Experiences compound.'
      ],

      venturesOverline: 'Portfolio',
      venturesTitle: 'Ventures',
      venturesViewAll: 'View All',

      studioOverline: 'Studio',
      studioTitle: 'Digital Products',
      studioDescription: 'Systems, templates, and playbooks for founders who build.',
      studioViewAll: 'Shop All Products',

      newsletterTitle: 'Stay Connected',
      newsletterDescription: 'Insights on experience design, founder systems, and brand presence. Monthly.',
      newsletterPlaceholder: 'Enter your email',
      newsletterButton: 'Subscribe',

      instagramCta: 'Follow me on Instagram',

      finalCtaHeadline: 'Ready to Work Together?',
      finalCtaDescription: 'Premium brand partnerships and client collaborations.',
      finalCtaButton: 'Partner With Me',

      marqueeText: ["it's about time you", "Took", "Up", "Space."]
    },
    es: {
      heroOverline: 'Fundadora / Directora Creativa',
      heroHeadline: 'Experiencias Que La Gente Recuerda',
      heroDescription: 'Momentos de campaña, producción en vivo y sistemas de experiencia. Arraigados en hospitalidad y excelencia operativa.',
      heroCta1: 'Colaboremos',
      heroCta2: 'Momentos Selectos',
      heroStats: ['4 Ventures', 'Santo Domingo + NYC', '10+ Años en Hospitalidad'],

      builderHeadline: 'CONSTRUYO EXPERIENCIAS, SISTEMAS Y MOMENTOS DE MARCA DESDE EL CONCEPTO HASTA LA EJECUCIÓN.',
      builderSubheadline: 'A través de hospitalidad, eventos y plataformas tecnológicas.',
      builderDescription: 'Libera el máximo potencial de tus eventos con diseño integral, planificación y dirección creativa adaptada a tus necesidades únicas.',
      builderCta: 'Explorar Servicios',
      builderPrompt: '¿Lista para crear algo inolvidable?',

      lanesTitle: 'Tres Carriles',
      lanesOverline: 'Dirección Creativa',
      lanes: [
        { num: '01', title: 'Campaña y Lanzamiento', outcome: 'Momentos de marca que viven más allá del momento.', bullets: ['Dirección creativa + despliegue', 'Producción de campaña', 'Arquitectura de contenido'] },
        { num: '02', title: 'Producción en Vivo', outcome: 'Eventos diseñados para impactar—y crear activos después.', bullets: ['Diseño de experiencias', 'Curación culinaria', 'Activaciones de marca'] },
        { num: '03', title: 'Sistemas de Experiencia', outcome: 'Excelencia operativa que escala.', bullets: ['Desarrollo de plataformas', 'Flujos de equipo', 'Diseño de procesos'] }
      ],

      manifestoTitle: 'Palabras Por Las Que Vivimos',
      manifestoLines: [
        'La hospitalidad entrenó el estándar.',
        'Los sistemas lo protegen a escala.',
        'La intención supera la perfección.',
        'Las experiencias se acumulan.'
      ],

      venturesOverline: 'Portafolio',
      venturesTitle: 'Ventures',
      venturesViewAll: 'Ver Todos',

      studioOverline: 'Studio',
      studioTitle: 'Productos Digitales',
      studioDescription: 'Sistemas, plantillas y playbooks para fundadores que construyen.',
      studioViewAll: 'Ver Todos los Productos',

      newsletterTitle: 'Mantente Conectado',
      newsletterDescription: 'Perspectivas sobre diseño de experiencias, sistemas para fundadores y presencia de marca. Mensual.',
      newsletterPlaceholder: 'Ingresa tu correo',
      newsletterButton: 'Suscribirse',

      instagramCta: 'Sígueme en Instagram',

      finalCtaHeadline: '¿Lista para Trabajar Juntos?',
      finalCtaDescription: 'Alianzas de marca premium y colaboraciones con clientes.',
      finalCtaButton: 'Colaboremos',

      marqueeText: ["es hora de que", "Tomes", "Tu", "Espacio."]
    }
  };

  const t = content[language];

  return (
    <div className="bg-vainilla" data-testid="home-page">
      {/* Hero Section - Bold Editorial */}
      <section className="min-h-screen flex items-center bg-vainilla pt-20">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 text-center">
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold text-stone-900 leading-[0.95] mb-12">
            {t.heroHeadline}
          </h1>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <button 
                className="bg-terracota text-white px-10 py-5 text-base font-semibold rounded-full hover:bg-stone-900 transition-all duration-300 flex items-center gap-3 group"
                data-testid="home-hero-cta"
              >
                {t.heroCta1}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/selected-moments">
              <button className="border-2 border-stone-300 text-stone-900 px-10 py-5 text-base font-semibold rounded-full hover:border-terracota hover:text-terracota transition-all duration-300">
                {t.heroCta2}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Photo - Asymmetric Editorial */}
      <section className="bg-vainilla overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[700px]">
          {/* Founder Image - Fixed 4:5 Aspect Ratio */}
          <div className="relative h-[500px] lg:h-auto lg:min-h-[800px]">
            <div className="absolute inset-0 lg:inset-8">
              <div className="relative w-full h-full">
                {/* Offset Backplate */}
                <div 
                  className="absolute top-4 left-4 right-0 bottom-0 bg-champagne rounded-2xl"
                  style={{ zIndex: 0 }}
                />
                {/* Image Container */}
                <div 
                  className="relative w-full h-full rounded-2xl overflow-hidden"
                  style={{ zIndex: 1 }}
                >
                  <img 
                    src={ownerPhoto}
                    alt={ownerName}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: founderPosition }}
                  />
                </div>
                {/* Editorial Caption */}
                <div 
                  className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm px-5 py-3 rounded-lg shadow-sm"
                  style={{ zIndex: 2 }}
                >
                  <p className="font-serif text-lg text-stone-900 font-semibold">{ownerName}</p>
                  <p className="text-xs text-stone-500 tracking-wider uppercase">Founder / Creative Director</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col justify-center px-8 md:px-12 lg:px-16 xl:px-24 py-16 lg:py-24 bg-champagne">
            <p className="text-terracota text-sm md:text-base font-bold tracking-[0.15em] uppercase leading-relaxed mb-8">
              {t.builderHeadline}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-stone-900 leading-tight mb-6">
              {t.builderSubheadline}
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed mb-8 max-w-lg">
              {t.builderDescription}
            </p>
            <p className="text-stone-900 font-semibold mb-8">
              {t.builderPrompt}
            </p>
            <div>
              <Link to="/evoke">
                <button className="bg-stone-900 text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-terracota transition-all duration-300 rounded-full">
                  {t.builderCta}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Three Lanes - Dark Section */}
      <section className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-terracota text-sm font-bold tracking-[0.2em] uppercase mb-4">
              {language === 'es' ? 'Dirección Creativa' : 'Creative Direction'}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold">
              {language === 'es' ? 'Tres Carriles' : 'Three Lanes'}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {dynamicLanes.length > 0 ? (
              dynamicLanes.map((lane, index) => (
                <div 
                  key={lane.id} 
                  className="p-8 border border-stone-700 hover:border-terracota transition-all duration-300 rounded-2xl group flex flex-col h-full"
                  style={{ minHeight: '320px' }}
                >
                  <span className="text-terracota text-xs font-bold tracking-wider">
                    {language === 'es' ? (lane.overline_es || lane.overline) : lane.overline}
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mt-4 mb-4 group-hover:text-terracota transition-colors">
                    {language === 'es' ? (lane.title_es || lane.title) : lane.title}
                  </h3>
                  <p className="text-stone-400 mb-6 flex-grow">
                    {language === 'es' ? (lane.description_es || lane.description) : lane.description}
                  </p>
                  <ul className="space-y-3 mt-auto">
                    {(language === 'es' 
                      ? (lane.capabilities_es?.length > 0 ? lane.capabilities_es : lane.capabilities) 
                      : lane.capabilities
                    )?.slice(0, 4).map((item, idx) => (
                      <li key={idx} className="text-sm text-stone-500 flex items-center">
                        <span className="w-1.5 h-1.5 bg-terracota rounded-full mr-3 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              // Fallback to static content if no dynamic lanes
              t.lanes.map((lane) => (
                <div 
                  key={lane.num} 
                  className="p-8 border border-stone-700 hover:border-terracota transition-all duration-300 rounded-2xl group flex flex-col h-full"
                  style={{ minHeight: '320px' }}
                >
                  <span className="text-terracota text-xs font-bold tracking-wider">{lane.num}</span>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold text-white mt-4 mb-4 group-hover:text-terracota transition-colors">{lane.title}</h3>
                  <p className="text-stone-400 mb-6 flex-grow">{lane.outcome}</p>
                  <ul className="space-y-3 mt-auto">
                    {lane.bullets.map((item, idx) => (
                      <li key={idx} className="text-sm text-stone-500 flex items-center">
                        <span className="w-1.5 h-1.5 bg-terracota rounded-full mr-3 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Scrolling Text Marquee */}
      <TextMarquee 
        lines={t.marqueeText}
        speed={30}
        dark={false}
      />

      {/* Evoke Pillars */}
      <EvokePillars />

      {/* Manifesto - Dark Section */}
      <section className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-terracota text-sm font-bold tracking-[0.2em] uppercase mb-10">{t.manifestoTitle}</p>
          <div className="space-y-4">
            {t.manifestoLines.map((line, idx) => (
              <p key={idx} className="font-serif text-2xl md:text-3xl lg:text-4xl text-white/90 leading-snug italic">{line}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Image Marquee */}
      <section className="py-12 bg-vainilla overflow-hidden">
        <ImageMarquee images={galleryImages} speed={45} grayscale={true} />
      </section>

      {/* Ventures */}
      <section className="py-24 md:py-32 bg-vainilla">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <p className="text-terracota text-sm font-bold tracking-[0.2em] uppercase mb-4">{t.venturesOverline}</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900">{t.venturesTitle}</h2>
            </div>
            <Link to="/ventures" className="mt-6 md:mt-0">
              <span className="text-sm font-semibold text-stone-600 hover:text-terracota transition-colors flex items-center gap-2">
                {t.venturesViewAll} <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {ventures.map((venture) => (
              <Link 
                key={venture.id} 
                to={`/ventures/${venture.id}`} 
                className="group block p-8 bg-white border border-stone-200 rounded-2xl hover:border-terracota transition-all duration-300"
                style={{ boxShadow: 'var(--shadow-panel)' }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl font-bold text-stone-900 mb-2 group-hover:text-terracota transition-colors">{venture.name}</h3>
                    <p className="text-stone-600">{venture.tagline}</p>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-stone-400 group-hover:text-terracota group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Studio / Digital Products */}
      <section className="py-24 md:py-32 bg-champagne">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-terracota text-sm font-bold tracking-[0.2em] uppercase mb-4">{t.studioOverline}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 mb-4">
              {t.studioTitle}
            </h2>
            <p className="text-stone-600 text-xl max-w-xl mx-auto">
              {t.studioDescription}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {products.length > 0 ? products.map((product) => (
              <div 
                key={product.id} 
                className="p-8 bg-white rounded-2xl h-full flex flex-col transition-all duration-300 hover:-translate-y-1"
                style={{ boxShadow: 'var(--shadow-panel)', minHeight: '320px' }}
              >
                <span className="inline-block px-3 py-1 bg-champagne text-terracota text-xs font-bold tracking-wider uppercase rounded-full w-fit">
                  {language === 'en' ? product.category : (product.category_es || product.category)}
                </span>
                <h3 className="font-serif text-xl md:text-2xl font-bold text-stone-900 mt-4 mb-3">
                  {language === 'en' ? product.title : (product.title_es || product.title)}
                </h3>
                <p className="text-stone-600 text-sm mb-4 flex-grow leading-relaxed">
                  {language === 'en' ? product.outcome : (product.outcome_es || product.outcome)}
                </p>
                <div className="flex items-center justify-between pt-5 border-t border-stone-200 mt-auto">
                  <span className="font-serif text-3xl font-bold text-stone-900">${product.price}</span>
                  <Link to="/studio">
                    <button className="text-sm font-semibold text-terracota hover:text-stone-900 transition-colors flex items-center gap-2">
                      {language === 'es' ? 'Ver Más' : 'Learn More'} <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            )) : (
              <div className="col-span-3 text-center py-12 text-stone-500">
                {language === 'es' ? 'Cargando productos...' : 'Loading products...'}
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/studio">
              <button className="border-2 border-stone-900 text-stone-900 px-10 py-4 text-sm font-semibold rounded-full hover:bg-stone-900 hover:text-white transition-all duration-300">
                {t.studioViewAll}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-vainilla">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-stone-900 rounded-2xl p-10 md:p-12 text-center text-white">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">{t.newsletterTitle}</h2>
            <p className="text-stone-400 mb-8">{t.newsletterDescription}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <input 
                type="email" 
                placeholder={t.newsletterPlaceholder}
                className="flex-1 px-5 py-4 bg-stone-800 border border-stone-700 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota transition-colors rounded-full"
              />
              <button className="bg-terracota text-white px-8 py-4 font-semibold hover:bg-white hover:text-stone-900 transition-all rounded-full">
                {t.newsletterButton}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Bar */}
      <section className="py-6 bg-champagne">
        <div className="max-w-6xl mx-auto px-6">
          <a 
            href="https://instagram.com/margaretnunez" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 group"
          >
            <span className="text-stone-900 font-semibold">{t.instagramCta}</span>
            <span className="text-terracota group-hover:translate-x-1 transition-transform">→</span>
            <span className="text-stone-500">@margaretnunez</span>
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{t.finalCtaHeadline}</h2>
          <p className="text-xl md:text-2xl text-stone-400 mb-12">{t.finalCtaDescription}</p>
          <Link to="/contact">
            <button 
              className="bg-terracota text-white px-12 py-6 text-lg font-semibold rounded-full hover:bg-white hover:text-stone-900 transition-all duration-300 flex items-center gap-3 mx-auto"
              data-testid="home-final-cta"
            >
              {t.finalCtaButton}
              <ArrowRight className="w-6 h-6" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NewHome;
